import React, { useEffect, useRef } from 'react';
import cn from '../../lib/cn';
import site from '../../data/site';
import navLinks from '../../data/nav';
import Logo from './Logo';
import Icon from '../ui/Icon';
import Button from '../ui/Button';
import DotTrail from '../ui/DotTrail';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';

/** Full-screen navigation drawer for tablet and phone. */
export function MobileMenu({ open, onClose, active, sectionHref = (id) => `#${id}` }) {
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  useLockBodyScroll(open);

  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement;
      panelRef.current?.focus();
    } else if (openerRef.current instanceof HTMLElement) {
      openerRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-[70] lg:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none'
      )}
      aria-hidden={!open}
      // The panel stays mounted so it can slide, so it must be made inert when
      // closed — otherwise its links remain in the tab order off-screen.
      inert={!open}
    >
      <div
        className={cn(
          'absolute inset-0 bg-ink-950/60 backdrop-blur-sm transition-opacity duration-400',
          open ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal={open}
        aria-label="Menu"
        className={cn(
          'absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-canvas shadow-lift outline-none',
          'transition-transform duration-500 ease-silk',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between border-b border-ink-900/10 px-6 py-5">
          <Logo markSize={38} showTagline={false} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="grid h-11 w-11 place-items-center rounded-full border border-ink-900/15 text-ink-900 transition hover:border-ink-900/50"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-6 py-8">
          <ul className="flex flex-col">
            {navLinks.map((link, i) => (
              <li key={link.id} className="border-b border-ink-900/8 last:border-0">
                <a
                  href={sectionHref(link.id)}
                  onClick={onClose}
                  className={cn(
                    'flex items-baseline gap-4 py-4 font-display text-3xl transition-colors',
                    active === link.id ? 'text-ink-950' : 'text-ink-700 hover:text-ink-950'
                  )}
                >
                  <span className="font-sans text-[0.6rem] tabular-nums tracking-widest text-champagne-500">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <DotTrail count={6} className="mt-8" />

          <div className="mt-8 flex flex-col gap-3">
            <Button href={site.contact.whatsappHref} variant="solid" size="md" className="w-full">
              <Icon name="whatsapp" size={16} />
              Enquire on WhatsApp
            </Button>
            <Button href={site.contact.phoneHref} variant="outline" size="md" className="w-full">
              <Icon name="phone" size={15} />
              {site.contact.phoneDisplay}
            </Button>
          </div>

          <div className="mt-8 space-y-1 text-sm leading-relaxed text-ink-700/80">
            <p className="eyebrow mb-2">Showroom</p>
            <p>{site.address.line1}</p>
            <p>{site.address.line2}</p>
            <p>
              {site.address.city}, {site.address.state} {site.address.postalCode}
            </p>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default MobileMenu;
